/*
 * Copyright (C) 2021 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */
import React, { PureComponent } from 'react';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import moment from 'moment-timezone';
import { Button } from '@instructure/ui-buttons';
import { colors } from '@instructure/canvas-theme';
import { IconWarningLine } from '@instructure/ui-icons';
import { PresentationContent } from '@instructure/ui-a11y-content';
import { themeable } from '@instructure/ui-themeable';
import { Spinner } from '@instructure/ui-spinner';
import { ToggleDetails } from '@instructure/ui-toggle-details';
import { View } from '@instructure/ui-view';
import { courseShape, opportunityShape } from '../plannerPropTypes';
import formatMessage from '../../format-message';
import PlannerItem from '../PlannerItem';
import responsiviser from '../responsiviser';
const styles = {
  componentId: 'dauOi',
  template: function (theme) {
    return `

.MissingAssignments-styles__root {
  display: flex;
  align-items: center;
  position: relative;
  margin-top: ${theme.toggleMarginTop || 'inherit'};
}

  .MissingAssignments-styles__root > :last-child {
    width: 100%;
  }

  .MissingAssignments-styles__root > div > button {
    margin: ${theme.toggleButtonMarginTop || 'inherit'} 0;
  }

  .MissingAssignments-styles__root > div > button svg {
      margin-inline-start: ${theme.toggleButtonMarginStart || 'inherit'};
    }

  [dir="ltr"] .MissingAssignments-styles__root > div > button svg {
      margin-left: ${theme.toggleButtonMarginStart || 'inherit'};
    }

  [dir="rtl"] .MissingAssignments-styles__root > div > button svg {
      margin-right: ${theme.toggleButtonMarginStart || 'inherit'};
    }

.MissingAssignments-styles__icon {
  position: absolute;
  top: calc(${theme.toggleMarginTop || 'inherit'} - 2px);
  left: 0;
}

.MissingAssignments-styles__medium.MissingAssignments-styles__root, .MissingAssignments-styles__small.MissingAssignments-styles__root {
    margin-inline-start: 0;
  }

[dir="ltr"] .MissingAssignments-styles__medium.MissingAssignments-styles__root,
[dir="ltr"] .MissingAssignments-styles__small.MissingAssignments-styles__root {
    margin-left: 0;
  }

[dir="rtl"] .MissingAssignments-styles__medium.MissingAssignments-styles__root,
[dir="rtl"] .MissingAssignments-styles__small.MissingAssignments-styles__root {
    margin-right: 0;
  }

.MissingAssignments-styles__moreButton {
  margin-top: ${theme.moreButtonMarginVertical || 'inherit'};
  margin-bottom: ${theme.moreButtonMarginVertical || 'inherit'};
  margin-inline-start: ${theme.moreButtonMarginStart || 'inherit'};
}

[dir="ltr"] .MissingAssignments-styles__moreButton {
  margin-left: ${theme.moreButtonMarginStart || 'inherit'};
}

[dir="rtl"] .MissingAssignments-styles__moreButton {
  margin-right: ${theme.moreButtonMarginStart || 'inherit'};
}
`;
  },
  'root': 'MissingAssignments-styles__root',
  'icon': 'MissingAssignments-styles__icon',
  'medium': 'MissingAssignments-styles__medium',
  'small': 'MissingAssignments-styles__small',
  'moreButton': 'MissingAssignments-styles__moreButton'
};
import theme from './theme';
export const convertSubmissionType = submissionTypes => {
  if ((submissionTypes === null || submissionTypes === void 0 ? void 0 : submissionTypes.length) > 0) {
    switch (submissionTypes[0]) {
      case 'discussion_topic':
        return 'Discussion';

      case 'online_quiz':
        return 'Quiz';
    }
  }

  return 'Assignment';
};
export const getMissingItemsText = (isExpanded, count) => {
  return isExpanded ? formatMessage(`{
                  count, plural,
                  one {Hide # missing item}
                  other {Hide # missing items}
               }`, {
    count
  }) : formatMessage(`{
                  count, plural,
                  one {Show # missing item}
                  other {Show # missing items}
               }`, {
    count
  });
};

function MissingAssignment({
  id,
  name,
  points_possible,
  html_url,
  due_at,
  submission_types,
  timeZone,
  course = {}
}) {
  return /*#__PURE__*/React.createElement(PlannerItem, {
    id: id,
    uniqueId: id,
    title: name,
    courseName: course.originalName,
    color: course.color,
    points: points_possible,
    html_url: html_url,
    date: moment(due_at).tz(timeZone),
    timeZone: timeZone,
    associated_item: convertSubmissionType(submission_types),
    simplifiedControls: true,
    isMissingItem: true
  });
}

MissingAssignment.propTypes = {
  id: string.isRequired,
  name: string.isRequired,
  points_possible: number.isRequired,
  html_url: string.isRequired,
  due_at: string.isRequired,
  submission_types: arrayOf(string).isRequired,
  timeZone: string.isRequired,
  course: shape(courseShape)
}; // Keeping this around until we're 100% sure we don't need it

export function NextMissingAssignmentsLink({
  hasMore,
  loadMoreOpportunities,
  loading
}) {
  if (!hasMore) return null;
  if (loading) return /*#__PURE__*/React.createElement(Spinner, {
    renderTitle: () => formatMessage('Loading more missing assignments'),
    size: "small"
  });
  return /*#__PURE__*/React.createElement(Button, {
    variant: "link",
    onClick: () => loadMoreOpportunities()
  }, formatMessage('Show More'));
}
NextMissingAssignmentsLink.propTypes = {
  hasMore: bool.isRequired,
  loadMoreOpportunities: func.isRequired,
  loading: bool.isRequired
}; // Themeable doesn't support pure functional components

export class MissingAssignments extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      expanded: false
    };
  }

  render() {
    const _this$props = this.props,
          courses = _this$props.courses,
          loadingOpportunities = _this$props.loadingOpportunities,
          opportunities = _this$props.opportunities,
          timeZone = _this$props.timeZone,
          _this$props$responsiv = _this$props.responsiveSize,
          responsiveSize = _this$props$responsiv === void 0 ? 'large' : _this$props$responsiv;
    const _opportunities$items = opportunities.items,
          items = _opportunities$items === void 0 ? [] : _opportunities$items;

    if (items.length === 0) {
      return null;
    }

    return /*#__PURE__*/React.createElement("section", {
      className: classnames(styles.root, styles[responsiveSize])
    }, !this.state.expanded && /*#__PURE__*/React.createElement("div", {
      className: styles.icon,
      "data-testid": "warning-icon"
    }, /*#__PURE__*/React.createElement(View, {
      margin: "0 small 0 0"
    }, /*#__PURE__*/React.createElement(PresentationContent, null, /*#__PURE__*/React.createElement(IconWarningLine, {
      color: "error"
    })))), /*#__PURE__*/React.createElement(ToggleDetails, {
      expanded: this.state.expanded,
      fluidWidth: true,
      onToggle: (_, expanded) => this.setState({
        expanded
      }),
      summary: /*#__PURE__*/React.createElement(View, {
        margin: "0 0 0 x-small"
      }, getMissingItemsText(this.state.expanded, items.length)),
      theme: {
        textColor: colors.textBrand,
        iconColor: colors.brand
      }
    }, /*#__PURE__*/React.createElement(View, {
      as: "div",
      borderWidth: "small none none none"
    }, items.map(opp => /*#__PURE__*/React.createElement(MissingAssignment, Object.assign({
      key: opp.id
    }, opp, {
      course: courses.find(c => c.id === opp.course_id),
      timeZone: timeZone
    })))), /*#__PURE__*/React.createElement("div", {
      className: styles.moreButton
    }, /*#__PURE__*/React.createElement(NextMissingAssignmentsLink, {
      hasMore: false,
      loadMoreOpportunities: () => {},
      loading: loadingOpportunities
    }))));
  }

}
MissingAssignments.propTypes = {};
MissingAssignments.propTypes = {
  courses: arrayOf(shape(courseShape)).isRequired,
  loadingOpportunities: bool.isRequired,
  opportunities: shape(opportunityShape).isRequired,
  timeZone: string.isRequired,
  responsiveSize: string
};

const mapStateToProps = ({
  courses,
  loading: {
    loadingOpportunities
  },
  opportunities
}) => ({
  courses,
  loadingOpportunities,
  opportunities
});

const ResponsiveMissingAssignment = responsiviser()(MissingAssignments);
const ThemeableMissingAssignments = themeable(theme, styles)(ResponsiveMissingAssignment);
const ConnectedMissingAssignments = connect(mapStateToProps)(ThemeableMissingAssignments);
ConnectedMissingAssignments.theme = ThemeableMissingAssignments.theme;
export default ConnectedMissingAssignments;